import React, { useEffect, useState, useRef } from 'react';
import { PaymentElement, useElements, useStripe, Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { CreditPackageProduct } from '../../models/CreditPackageProduct';

interface StripeWrapperProps {
  clientSecret: string | null;
  selectedProduct: CreditPackageProduct | null;
  buyerName: string;
  buyerEmail: string;
  committedEmail: string;
  isProcessing: boolean;
  isGift: boolean;
  onSuccess: (transactionId: string) => void;
  onError: (error: string) => void;
  onPaymentStart: () => void;
  onPaymentEnd: () => void;
}

const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null;

const StripePaymentForm: React.FC<StripeWrapperProps> = ({
  clientSecret,
  selectedProduct,
  buyerName,
  buyerEmail,
  committedEmail,
  isProcessing,
  isGift,
  onSuccess,
  onError,
  onPaymentStart,
  onPaymentEnd
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string>('');

  const handlePaymentCompletion = async () => {
    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setError('');

    try {
      onPaymentStart();
      
      // Submit the PaymentElement to validate and collect payment method details
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message ?? 'Payment form validation failed');
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/thank-you?isGift=${encodeURIComponent(isGift)}`,
          payment_method_data: {
            billing_details: {
              name: buyerName,
              email: buyerEmail,
            },
          },
        },
        redirect: 'if_required',
      });

      if (error) {
        throw new Error(error.message ?? 'Unable to confirm payment');
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Calling onSuccess un-awaited inside this try is safe ONLY because the credits handler
        // is synchronous (Checkout.tsx does a dataLayer.push and a navigate — it cannot reject).
        // An ASYNC handler must be awaited: its promise would otherwise float, its rejection
        // would settle after this function returns and escape the catch below, and a charged
        // customer would be left with no error and a live Pay button. That is exactly the bug
        // that shipped when this block was copied into the guest-seat booking flow.
        onSuccess(paymentIntent.id);
      } else if (paymentIntent && paymentIntent.status === 'requires_action') {
        // Payment requires additional authentication, Stripe will handle redirect
        return;
      } else {
        throw new Error('Payment was not successful');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Payment failed. Please try again in a moment.';
      setError(message);
      onError(message);
    } finally {
      onPaymentEnd();
    }
  };

  const handlePayButtonClick = () => {
    if (clientSecret && stripe && elements) {
      handlePaymentCompletion();
    }
  };

  return (
    <div className="mt-4">
      <label className="form-label text-accent">Payment details (powered by Stripe)</label>
      {clientSecret ? (
        <>
          <div className="card-element-wrapper">
            <PaymentElement
              options={{
                layout: 'tabs',
                wallets: {
                  applePay: 'auto',
                  googlePay: 'auto',
                },
              }}
            />
          </div>
          <button 
            className="btn btn-primary w-100 mt-4" 
            type="button" 
            onClick={handlePayButtonClick}
            disabled={isProcessing || !selectedProduct}
          >
            {isProcessing ? 'Processing payment...' : selectedProduct ? `Pay $${selectedProduct.price.toFixed(2)}` : 'Please select a package'}
          </button>
        </>
      ) : null}

      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

const StripeWrapper: React.FC<StripeWrapperProps> = (props) => {
  const paymentSectionRef = useRef<HTMLDivElement>(null);

  // Scroll to payment section when card input appears
  useEffect(() => {
    if (props.clientSecret && paymentSectionRef.current) {
      // Small delay to ensure the element is fully rendered
      setTimeout(() => {
        paymentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [props.clientSecret]);

  if (!stripePromise || !props.clientSecret) {
    return null;
  }

  return (
    <div ref={paymentSectionRef}>
      <Elements
        key={props.clientSecret}
        stripe={stripePromise}
        options={{
          clientSecret: props.clientSecret,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#128200',
              colorBackground: '#ffffff',
              colorText: '#333333',
              colorDanger: '#ed1c24',
              fontFamily: 'Roboto Condensed, sans-serif',
              fontSizeBase: '16px',
              spacingUnit: '4px',
              borderRadius: '8px',
            },
            rules: {
              '.Input': {
                backgroundColor: '#ffffff',
                border: '1px solid #cccccc',
                color: '#333333'
              },
              '.Input:focus': {
                borderColor: '#128200',
                boxShadow: '0 0 0 0.2rem rgba(18, 130, 0, 0.25)',
              },
              '.Input--invalid': {
                borderColor: '#ed1c24',
              },
              '.Label': {
                color: '#000000'
              },
              '.Tab': {
                backgroundColor: 'transparent',
                border: '1px solid #cccccc',
                color: '#333333'
              },
              '.Tab--selected': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                borderColor: '#000000',
                color: '#000000'
              },
            },
          }
        }}
      >
        <StripePaymentForm {...props} />
      </Elements>
    </div>
  );
};

export default StripeWrapper;

